import { downloadFile } from "@/shared/download";
import type { ReturnRecord } from "./api";

/**
 * Apre/scarica l'etichetta PDF di un reso/sostituzione in modo
 * **coesistenza-safe** (desktop Electron ↔ web), stesso pattern del download
 * LDV in ``OrdiniPage``:
 *
 * - **desktop** (Electron): ``omBridge.openPath`` apre il file col programma
 *   default di Windows (whitelisted a ``%LOCALAPPDATA%/OrderManager``). Serve
 *   ``label_path`` (percorso su disco del backend locale).
 * - **web** (nessun bridge, o manca ``label_path``): il browser non apre
 *   ``file://`` → scarichiamo via endpoint HTTP assoluto ``/api/returns/{id}/label``.
 *
 * In entrambi i rami un fallimento **lancia**: i chiamanti mostrano il banner
 * in italiano (``prettifyApiError``) invece di non far succedere niente.
 */
export async function openReturnLabel(
  record: Pick<ReturnRecord, "id" | "label_path">,
): Promise<void> {
  const bridge = window.omBridge;
  if (bridge?.openPath && record.label_path) {
    // openPath risolve con "" se ok, o con un messaggio di sistema se il file
    // manca o il percorso non e' whitelisted: propaghiamo l'errore cosi' il
    // chiamante puo' mostrarlo (stesso pattern di handleGenerateLabel), invece
    // di fallire in modo silenzioso.
    const result = await bridge.openPath(record.label_path);
    if (result) throw new Error(result);
    return;
  }
  // Niente ``window.open``: non lancia MAI. Ritorna ``null`` se il popup e'
  // bloccato (il ``.catch`` dei chiamanti non scatta -> "non succede niente")
  // e su 404/500 apre una scheda con l'errore grezzo del backend invece del
  // banner in italiano della pagina. Stesso pattern di ``downloadDisputeFile``:
  // fetch esplicita -> throw leggibile, poi anchor ``download``, che nel
  // browser apre il Save-As e in Electron passa da ``will-download``.
  const path = `/api/returns/${record.id}/label`;
  await downloadFile(path, `etichetta_reso_${record.id}.pdf`);
}

/** Solo il borderò BRT della pratica (Davide 22/10, «il borderò??»): stava in
 *  coda all'etichetta e non si vedeva. Lancia su 404 come ``openReturnLabel``. */
export async function openReturnBordero(record: Pick<ReturnRecord, "id">): Promise<void> {
  await downloadFile(`/api/returns/${record.id}/bordero`, `bordero_reso_${record.id}.pdf`);
}
